import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchAdminPredictivePricing,
  recalculateAllPredictivePrices,
  recalculateListingPredictivePrice,
} from "../../../../services/vehiclePredictivePricingAdminApi";
import { vehicleMarketDetailPath } from "../../../../constants/transport";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import { formatCop } from "../../mobility/vehicleMarketFormat";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const VehiclePredictivePricingAdminPage = ({ onClose }) => {
  const [overview, setOverview] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminPredictivePricing({ limit: 200 });
      setOverview(data.overview || null);
      setListings(data.listings || []);
    } catch (err) {
      setError(err.message || "Unable to load predictive pricing dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRecalcAll = async () => {
    try {
      const r = await recalculateAllPredictivePrices({ limit: 500 });
      setToast(`Recalculated ${r.result?.processed ?? 0} listings.`);
      load();
    } catch (err) {
      setToast(err.message || "Recalculation failed.");
    }
  };

  const handleRecalcRow = async (id) => {
    try {
      await recalculateListingPredictivePrice(id);
      setToast("Prediction recalculated.");
      load();
    } catch (err) {
      setToast(err.message || "Recalculation failed.");
    }
  };

  const active = overview?.activeModel;

  return (
    <div className="admin-dashboard admin-vehicle-predictive-dash admin-vehicle-sources-dash--full-width">
      <header className="admin-dashboard__header">
        <div>
          <h1>Predictive Pricing</h1>
          <p className="muted">Fair Value v2 — model-based estimated market prices (M24).</p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button type="button" className="crm-btn crm-btn-secondary" onClick={handleRecalcAll}>
            Recalculate batch
          </button>
          {onClose ? (
            <button type="button" className="crm-btn crm-btn-secondary" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {toast ? <p className="admin-toast">{toast}</p> : null}
      {loading ? <AdminStatePanel state="loading" message="Loading predictive pricing…" /> : null}
      {error ? <AdminStatePanel state="error" message={error} onRetry={load} /> : null}

      {!loading && !error ? (
        <>
          <div className="admin-summary-cards">
            <div className="admin-summary-card">
              <span>Active model</span>
              <strong>{active?.modelKey || "—"}</strong>
            </div>
            <div className="admin-summary-card">
              <span>Model version</span>
              <strong>{active?.modelVersion || "—"}</strong>
            </div>
            <div className="admin-summary-card">
              <span>Training rows</span>
              <strong>{active?.trainingRowCount ?? "—"}</strong>
            </div>
            <div className="admin-summary-card">
              <span>Validation MAE</span>
              <strong>{active?.maeCop != null ? formatCop(active.maeCop) : "—"}</strong>
            </div>
            <div className="admin-summary-card">
              <span>Validation MAPE</span>
              <strong>{active?.mapePct != null ? `${active.mapePct}%` : "—"}</strong>
            </div>
            <div className="admin-summary-card">
              <span>Predictions created</span>
              <strong>{overview?.predictionsCreated ?? 0}</strong>
            </div>
            <div className="admin-summary-card">
              <span>High/medium confidence</span>
              <strong>{overview?.reliablePredictions ?? 0}</strong>
            </div>
            <div className="admin-summary-card">
              <span>Low confidence</span>
              <strong>{overview?.lowConfidencePredictions ?? 0}</strong>
            </div>
            <div className="admin-summary-card">
              <span>Out of distribution</span>
              <strong>{overview?.outOfDistributionListings ?? 0}</strong>
            </div>
            <div className="admin-summary-card">
              <span>Missing prediction</span>
              <strong>{overview?.listingsMissingPrediction ?? 0}</strong>
            </div>
          </div>

          <AdminTableContainer title="Listing predictions">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Source</th>
                  <th>Current</th>
                  <th>Predicted</th>
                  <th>Delta</th>
                  <th>Confidence</th>
                  <th>Fair Value v1</th>
                  <th>Agreement</th>
                  <th>Version</th>
                  <th>Calculated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {listings.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="muted">
                      No predictions yet. Train a model and run batch recalculation.
                    </td>
                  </tr>
                ) : (
                  listings.map((row) => (
                    <tr key={row.vehicleListingId}>
                      <td>{row.title}</td>
                      <td>{row.sourceName}</td>
                      <td>{formatCop(row.currentPriceCop)}</td>
                      <td>{formatCop(row.predictedPriceCop)}</td>
                      <td>
                        {row.priceVsPredictionCop != null
                          ? `${formatCop(row.priceVsPredictionCop)} (${row.priceVsPredictionPct ?? "—"}%)`
                          : "—"}
                      </td>
                      <td>{row.predictionConfidenceLabel || "—"}</td>
                      <td>{formatCop(row.fairValueCop)}</td>
                      <td>{row.fairValueComparisonStatus || "—"}</td>
                      <td>{row.predictiveModelVersion || "—"}</td>
                      <td>{formatDate(row.calculatedAt)}</td>
                      <td className="admin-table__actions">
                        <button
                          type="button"
                          className="crm-btn crm-btn-ghost"
                          onClick={() => handleRecalcRow(row.vehicleListingId)}
                        >
                          Recalculate
                        </button>
                        <Link
                          to={vehicleMarketDetailPath(row.vehicleListingId)}
                          className="crm-btn crm-btn-ghost"
                        >
                          Detail
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </AdminTableContainer>
        </>
      ) : null}
    </div>
  );
};

export default VehiclePredictivePricingAdminPage;
